import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpEventType,
  HttpResponse,
  HttpHeaders,
  HttpClient,
} from '@angular/common/http';
import { Subject, Observable, catchError, of, forkJoin } from 'rxjs';
import { HttpUtilService } from '@services/http-util.service';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100MB

  constructor(
    private http: HttpClient,
    private httpUtil: HttpUtilService
  ) {}

  /**
   * Calculate optimal chunk size based on file size
   * @param fileSize Size of the file in bytes
   * @returns Optimal chunk size in bytes
   */
  private calculateChunkSize(fileSize: number): number {
    // AWS S3 multipart upload requirements:
    // - Minimum chunk size: 5MB (except last part)
    // - Maximum chunk size: 5GB
    // - Maximum parts: 10,000
    // - Optimal performance: 100MB-1GB chunks

    if (fileSize <= 100 * 1024 * 1024) {
      // Files up to 100MB: use 5MB chunks
      return 5 * 1024 * 1024; // 5MB chunks
    } else if (fileSize <= 1 * 1024 * 1024 * 1024) {
      // Files up to 1GB: use 50MB chunks
      return 50 * 1024 * 1024; // 50MB chunks
    } else if (fileSize <= 10 * 1024 * 1024 * 1024) {
      // Files up to 10GB: use 100MB chunks
      return 100 * 1024 * 1024; // 100MB chunks
    } else if (fileSize <= 100 * 1024 * 1024 * 1024) {
      // Files up to 100GB: use 500MB chunks
      return 500 * 1024 * 1024; // 500MB chunks
    } else {
      // Very large files (>100GB): use 1GB chunks (max recommended for performance)
      return 1024 * 1024 * 1024; // 1GB chunks
    }
  }

  /**
   * Upload a file
   * @param file file to upload
   * @param route folder route to upload
   */
  public uploadSingle(
    file: File,
    route = '',
    parent?,
    forceUnique?,
    additionalQueryParams?: string
  ): Observable<any> {
    console.log(route);

    // Check if file size exceeds multipart threshold
    if (file.size > this.MULTIPART_THRESHOLD) {
      return this.uploadMultipart(
        file,
        route,
        parent,
        forceUnique,
        additionalQueryParams
      );
    } else {
      return this.uploadSinglePart(
        file,
        route,
        parent,
        forceUnique,
        additionalQueryParams
      );
    }
  }

  /**
   * Single-part upload for files smaller than 50MB
   */
  private uploadSinglePart(
    file: File,
    route = '',
    parent?,
    forceUnique?,
    additionalQueryParams?: string
  ): Observable<any> {
    // this will be the our resulting map
    let status: any = {};
    // create a new progress-subject for every file
    const upload = new Subject<any>();
    const url = route + '/' + file.name;
    status = {
      progress: 0,
      file: file.name,
      size: file.size,
      key: '',
      url: url,
      fileref: file,
      existing: false,
    };
    let qp = `?file_type=${file.type}`;
    if (additionalQueryParams) {
      qp += `&${additionalQueryParams}`;
    }
    if (forceUnique) qp += '&force_unique=true';

    this.httpUtil
      .request(
        'POST',
        environment.apiRoot + '/upload/' + url + qp,
        { parent },
        null,
        true
      )
      .pipe(catchError(error => of({ error })))
      .subscribe(f => {
        if (f?.error || f?.errorMessage) {
          console.log(f);
          status.error = f.error || f.errorMessage;
          upload.next(status);
          upload.complete();
        } else {
          status.fileName = f.newFileName || file.name;
          status.eurl = f.eurl;
          status.key = route
            ? 'uploads/' + route + f.newFileName
            : 'uploads' + f.newFileName;
          status.existing = f.existing;
          status.parentFolderId = f.parentFolderId;
          // create a new multipart-form for every file
          const formData: FormData = new FormData();
          formData.append('file', file, file.name);

          // create a http-post request and pass the form
          // tell it to report the upload progress
          let reqHeaders = new HttpHeaders();
          reqHeaders = reqHeaders.set('Content-type', file.type);
          const req = new HttpRequest('PUT', f.url, file, {
            headers: reqHeaders,
            reportProgress: true,
          });

          // send the http-request and subscribe for progress-updates
          this.http
            .request(req)
            .pipe(catchError(error => of({ error })))
            .subscribe((event: any) => {
              if (event?.error) {
                status.error = event.error;
                upload.next(status);
                upload.complete();
              } else if (event?.type === HttpEventType.UploadProgress) {
                const percentDone = event.total
                  ? Math.round((100 * event.loaded) / event.total)
                  : 0;

                status.progress = percentDone;
                upload.next(status);
              } else if (event instanceof HttpResponse) {
                status.progress = 100;
                upload.next(status);
                upload.complete();
              }
            });
        }
        // }
        // Save every progress-observable in a map of all observables
      });

    // return the map of progress.observables
    return upload;
  }

  //multipart upload for larger files
  private uploadMultipart(
    file: File,
    route = '',
    parent?,
    forceUnique?,
    additionalQueryParams?: string
  ): Observable<any> {
    const upload = new Subject<any>();
    const url = route + '/' + file.name;

    let status: any = {
      progress: 0,
      file: file.name,
      size: file.size,
      key: '',
      url: url,
      fileref: file,
      existing: false,
      multipart: true,
    };

    let qp = `?file_type=${file.type}&multipart=true`;
    if (additionalQueryParams) {
      qp += `&${additionalQueryParams}`;
    }
    if (forceUnique) qp += '&force_unique=true';

    this.httpUtil
      .request(
        'POST',
        environment.apiRoot + '/upload/' + url + qp,
        { parent },
        null,
        true
      )
      .pipe(catchError(error => of({ error })))
      .subscribe(async f => {
        if (f?.error || f?.errorMessage) {
          console.log(f);
          status.error = f.error || f.errorMessage;
          upload.next(status);
          upload.complete();
        } else {
          status.fileName = f.newFileName || file.name;
          status.key = route
            ? 'uploads/' + route + f.newFileName
            : 'uploads' + f.newFileName;
          status.existing = f.existing;
          status.parentFolderId = f.parentFolderId;
          status.uploadId = f.uploadId;
          status.bucket = f.bucket;
          status.eurl = f.eurl;

          this.performMultipartUpload(
            file,
            f.uploadId,
            url,
            status,
            upload
          ).subscribe({
            next: result => {
              status = { ...status, ...result };
              upload.next(status);
              upload.complete();
            },
            error: error => {
              status.error = error.message;
              upload.next(status);
              upload.complete();
            },
          });
        }
      });

    return upload;
  }

  /**
   * Perform the actual multipart upload
   */
  private performMultipartUpload(
    file: File,
    uploadId: string,
    url: string,
    status: any,
    upload: Subject<any>
  ): Observable<any> {
    const chunkSize = this.calculateChunkSize(file.size);
    const totalParts = Math.ceil(file.size / chunkSize);
    const uploadedParts: any[] = [];
    let uploadedBytes = 0;
    let completedParts = 0;
    const maxConcurrentParts = 5; // Upload 5 parts at a time for optimal performance
    const maxRetries = 3; // Maximum number of retry attempts per part

    console.log(
      `Starting multipart upload: ${file.name} (${file.size} bytes) with ${totalParts} parts of ${chunkSize} bytes each`
    );

    return new Observable(observer => {
      // Helper function to delay execution
      const delay = (ms: number) =>
        new Promise(resolve => setTimeout(resolve, ms));

      // Function to upload a single part with retry logic
      const uploadPart = async (
        partNumber: number,
        retryCount = 0
      ): Promise<any> => {
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        try {
          // Get signed URL for this part
          const partUrlResponse = await this.httpUtil
            .request(
              'PUT',
              environment.apiRoot + '/upload/' + url + '?operation=getPartUrl',
              { uploadId, partNumber },
              null,
              true
            )
            .pipe(catchError(error => of({ error })))
            .toPromise();

          if (partUrlResponse?.error) {
            throw new Error(partUrlResponse.error);
          }

          // Upload the part directly to S3
          const uploadResponse = await fetch(partUrlResponse.url, {
            method: 'PUT',
            body: chunk,
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
            },
          });

          if (!uploadResponse.ok) {
            throw new Error(
              `Failed to upload part ${partNumber}: ${uploadResponse.status} ${uploadResponse.statusText}`
            );
          }

          const etag = uploadResponse.headers.get('ETag');
          const partInfo = {
            PartNumber: partNumber,
            ETag: etag,
          };

          return partInfo;
        } catch (error) {
          // Retry logic with exponential backoff
          if (retryCount < maxRetries) {
            const backoffDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
            console.warn(
              `Part ${partNumber} upload failed, retrying in ${backoffDelay}ms (attempt ${retryCount + 1}/${maxRetries}):`,
              error
            );

            await delay(backoffDelay);
            return uploadPart(partNumber, retryCount + 1);
          } else {
            console.error(
              `Part ${partNumber} upload failed after ${maxRetries} attempts:`,
              error
            );
            throw new Error(
              `Failed to upload part ${partNumber} after ${maxRetries} retries: ${error.message}`
            );
          }
        }
      };

      // Main upload logic with sliding window concurrency and immediate abort on failure
      const uploadAllParts = async () => {
        let uploadAborted = false;
        let abortError: Error | null = null;

        // Function to abort the entire upload
        const abortUpload = async (error: Error) => {
          if (uploadAborted) return; // Prevent multiple abort attempts
          uploadAborted = true;
          abortError = error;

          console.error(
            'Aborting multipart upload due to part failure:',
            error
          );

          // Try to abort the upload on the server
          try {
            await this.httpUtil
              .request(
                'PUT',
                environment.apiRoot + '/upload/' + url + '?operation=abort',
                { uploadId },
                null,
                true
              )
              .pipe(catchError(() => of({})))
              .toPromise();
          } catch (abortError) {
            console.warn('Failed to abort multipart upload:', abortError);
          }

          // Emit error status
          status.error = error.message;
          status.progress = 0;
          upload.next(status);
          observer.error(error);
        };

        try {
          const activeUploads = new Map<number, Promise<any>>();
          let nextPartToStart = 1;

          // Function to start a new upload when a slot becomes available
          const startNextUpload = () => {
            if (
              uploadAborted ||
              nextPartToStart > totalParts ||
              activeUploads.size >= maxConcurrentParts
            ) {
              return null;
            }

            const partNumber = nextPartToStart++;
            const uploadPromise = uploadPart(partNumber)
              .then(partInfo => {
                if (uploadAborted) return null; // Don't process if aborted

                completedParts++;
                uploadedBytes += Math.min(
                  chunkSize,
                  file.size - (partNumber - 1) * chunkSize
                );
                const progress = Math.round((uploadedBytes / file.size) * 100);

                status.progress = Math.min(progress, 99); // Avoid 100% since that's for completion
                upload.next(status);

                // Add the successful part to the uploadedParts array
                if (partInfo) {
                  uploadedParts.push(partInfo);
                }

                return partInfo;
              })
              .catch(async error => {
                // If a part fails after all retries, abort the entire upload immediately
                console.error(
                  `Critical error uploading part ${partNumber}:`,
                  error
                );
                await abortUpload(error);
                return null;
              })
              .finally(() => {
                // Remove from active uploads when done
                activeUploads.delete(partNumber);

                // Only try to start next upload if not aborted
                if (!uploadAborted) {
                  startNextUpload();
                }
              });

            activeUploads.set(partNumber, uploadPromise);
            return uploadPromise;
          };

          // Start initial batch of uploads
          for (let i = 0; i < Math.min(maxConcurrentParts, totalParts); i++) {
            startNextUpload();
          }

          // Wait for ALL parts to complete, not just the initial batch
          const allPartPromises: Promise<any>[] = [];

          // Create a promise that resolves when all parts are complete
          const waitForAllParts = new Promise<any[]>((resolve, reject) => {
            const checkCompletion = () => {
              if (uploadAborted) {
                reject(new Error('Upload aborted'));
                return;
              }

              if (completedParts === totalParts) {
                // All parts completed, resolve with the uploaded parts
                resolve([...uploadedParts]); // Return a copy of the array
              } else {
                // Still waiting for more parts, check again in 100ms
                setTimeout(checkCompletion, 100);
              }
            };
            checkCompletion();
          });

          // Wait for all parts to complete
          const allParts = await waitForAllParts;

          // Complete multipart upload
          const completeResponse = await this.httpUtil
            .request(
              'PUT',
              environment.apiRoot + '/upload/' + url + '?operation=complete',
              { uploadId, parts: allParts },
              null,
              true
            )
            .pipe(catchError(error => of({ error })))
            .toPromise();

          if (completeResponse?.error) {
            throw new Error(completeResponse.error);
          }

          observer.next({ ...status, progress: 100 });
          observer.complete();
        } catch (error) {
          if (!uploadAborted) {
            // Only handle error if not already aborted
            await abortUpload(error as Error);
          }
        }
      };

      // Start the parallel upload process
      uploadAllParts();
    });
  }
}
